/**
 * Header Component
 * 
 * Cabeçalho do sistema ERP.
 * Exibe informações do usuário, notificações e ações rápidas.
 * 
 * Funcionalidades:
 * - Informações do usuário logado
 * - Menu de usuário
 * - Notificações
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, MessageSquare, Package, ChartSpline, BrainCircuit, Map, PenLine } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useIsMobileTablet } from '../../../hooks/useIsMobileTablet'
import { messageService, accessGroupService } from '../../../services/erpApi'
import { authService } from '../../../services/api'
import UserMenu from '../../../components/UserMenu/UserMenu'
import HeaderThemeSwitch from './HeaderThemeSwitch'
import ErpPeriodDropdown from '../../../components/ERP/ErpPeriodDropdown/ErpPeriodDropdown'
import './Header.css'

const DUTY_MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

function dutyRosterPath(notification) {
  const subject = String(notification?.subject || '')
  if (!subject.startsWith('Seus plantões')) return ''
  const source = `${subject}\n${notification?.last_message_content || ''}`
  const match = source.match(/(Janeiro|Fevereiro|Março|Abril|Maio|Junho|Julho|Agosto|Setembro|Outubro|Novembro|Dezembro)(?: de)? (\d{4})/)
  if (!match) return '/erp/minha-escala'
  const month = DUTY_MONTHS.indexOf(match[1]) + 1
  return `/erp/minha-escala?ano=${match[2]}&mes=${month}`
}

function onlineSalesPath(notification) {
  const subject = String(notification?.subject || '')
  if (subject.startsWith('Pagamento confirmado') || subject.startsWith('Novo Pedido Online')) {
    return '/erp/vendas/online'
  }
  return ''
}

function Header({
  sidebarCollapsed,
  isSidebarExpanded,
  assistantOpen,
  onToggleAssistant,
  operationalAssistantOpen,
  onToggleOperationalAssistant,
}) {
  const { user, organizations, currentSchema, switchOrganization } = useAuth()
  const [switchingOrg, setSwitchingOrg] = useState(false)
  const navigate = useNavigate()
  const isMobileTablet = useIsMobileTablet()
  const { hasPermission } = usePermissions()
  const [showNotifications, setShowNotifications] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [profileReminder, setProfileReminder] = useState(null)
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const notificationsRef = useRef(null)
  const previousUnreadCountRef = useRef(0) // Para rastrear mudanças no contador

  const isExpanded = isSidebarExpanded ?? !sidebarCollapsed
  const canAccessMessages = hasPermission(MODULES.MESSAGES, ACTIONS.VIEW)
  const canAccessSellerAssistant = hasPermission(MODULES.SALES, ACTIONS.SALES_SELLER_ASSISTANT)
  const canAccessOperationalAssistant = hasPermission(MODULES.ERP, ACTIONS.ERP_OPERATIONAL_ASSISTANT)
  const [isDiretorFinanceiro, setIsDiretorFinanceiro] = useState(false)

  // Verificar se o usuário está no grupo "Diretor Financeiro"
  useEffect(() => {
    const checkDiretorFinanceiro = async () => {
      if (!user?.access_group_id) {
        setIsDiretorFinanceiro(false)
        return
      }

      try {
        const response = await accessGroupService.getById(user.access_group_id)
        if (response.success && response.data) {
          const groupName = (response.data.name || '').trim().toLowerCase()
          setIsDiretorFinanceiro(groupName === 'diretor financeiro')
        } else {
          setIsDiretorFinanceiro(false)
        }
      } catch (error) {
        console.error('Erro ao verificar grupo de acesso:', error)
        setIsDiretorFinanceiro(false)
      }
    }

    checkDiretorFinanceiro()
  }, [user?.access_group_id])

  // Função para carregar notificações usando useCallback para evitar recriações
  const loadNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true)
      const response = await messageService.getConversations({ unreadOnly: true, limit: 10 })
      
      if (response.success) {
        const notificationsData = response.data || []
        setNotifications(notificationsData)
      } else {
        setNotifications([])
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error)
      setNotifications([])
    } finally {
      setLoadingNotifications(false)
    }
  }, [])

  const loadProfileReminder = useCallback(async () => {
    try {
      const response = await authService.getProfileReminder()
      setProfileReminder(response?.success && response.data ? response.data : null)
    } catch (error) {
      console.error('Erro ao carregar aviso do perfil profissional:', error)
      setProfileReminder(null)
    }
  }, [])

  useEffect(() => {
    loadProfileReminder()
    const interval = setInterval(loadProfileReminder, 30000)
    const refresh = () => loadProfileReminder()
    window.addEventListener('wfmed-profile-updated', refresh)
    return () => {
      clearInterval(interval)
      window.removeEventListener('wfmed-profile-updated', refresh)
    }
  }, [loadProfileReminder])

  // Carregar contador de mensagens não lidas
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const response = await messageService.getUnreadCount()
        
        if (response.success && response.data) {
          const newCount = response.data.count || 0
          const oldCount = previousUnreadCountRef.current
          
          // Se o contador mudou e o dropdown está aberto, recarregar notificações
          if (newCount !== oldCount && showNotifications) {
            loadNotifications()
          }
          
          // Atualizar o contador e a ref
          setUnreadCount(newCount)
          previousUnreadCountRef.current = newCount
        }
      } catch (error) {
        console.error('Erro ao carregar contador de mensagens:', error)
      }
    }

    loadUnreadCount()
    // Atualizar a cada 30 segundos
    const interval = setInterval(loadUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [showNotifications, loadNotifications])

  // Carregar notificações quando o dropdown é aberto
  useEffect(() => {
    if (!showNotifications) return
    loadProfileReminder()
    loadNotifications()
  }, [showNotifications, loadNotifications, loadProfileReminder])

  // Recarregar notificações periodicamente quando o dropdown está aberto
  useEffect(() => {
    if (!showNotifications) return

    const interval = setInterval(() => {
      loadNotifications()
    }, 15000) // Atualizar a cada 15 segundos quando o dropdown está aberto

    return () => clearInterval(interval)
  }, [showNotifications, loadNotifications])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
    }

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showNotifications])

  const handleMessagesClick = () => {
    navigate('/erp/mensagens')
  }

  const handleAssistantClick = () => {
    if (isMobileTablet) {
      navigate('/erp/assistente-vendas')
      return
    }
    onToggleAssistant?.()
  }

  const handleOperationalAssistantClick = () => {
    if (isMobileTablet) {
      navigate('/erp/assistente-operacional')
      return
    }
    onToggleOperationalAssistant?.()
  }

  const handleNotificationClick = (notification) => {
    setShowNotifications(false)
    if (notification?.href) {
      navigate(notification.href)
      return
    }
    const destination = dutyRosterPath(notification) || onlineSalesPath(notification)
    if (destination) {
      const id = notification.id
      setNotifications((current) => current.filter((item) => item.id !== id))
      setUnreadCount((count) => Math.max(0, count - 1))
      previousUnreadCountRef.current = Math.max(0, previousUnreadCountRef.current - 1)
      messageService.markConversationAsRead(id).catch((error) => {
        console.error('Erro ao marcar aviso como lido:', error)
      })
      navigate(destination)
      return
    }
    if (canAccessMessages) navigate(`/erp/mensagens?conversation=${notification.id}`)
  }

  const handleFinanceDashboardClick = () => {
    navigate('/erp/financeiro/dashboard')
  }

  const formatTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Agora'
    if (diffMins < 60) return `${diffMins} min atrás`
    if (diffHours < 24) return `${diffHours}h atrás`
    if (diffDays < 7) return `${diffDays}d atrás`
    return date.toLocaleDateString('pt-BR')
  }

  const notificationCount = unreadCount + (profileReminder ? 1 : 0)

  return (
    <header className={`erp-header ${!isExpanded ? 'sidebar-collapsed' : ''}`}>
      <div className="header-right">
        {organizations.length > 1 && (
          <ErpPeriodDropdown
            label=""
            ariaLabel="Trocar de organização"
            className="header-org-switch"
            align="left"
            portal
            value={currentSchema || user?.organization_schema || ''}
            disabled={switchingOrg}
            options={organizations.map((organization) => ({
              value: organization.schema_name,
              label: organization.label,
            }))}
            onChange={async (nextSchema) => {
              if (!nextSchema || nextSchema === (currentSchema || user?.organization_schema)) return
              setSwitchingOrg(true)
              try {
                await switchOrganization(nextSchema)
              } catch (error) {
                setSwitchingOrg(false)
                window.alert(error.message || 'Não foi possível trocar de organização')
              }
            }}
          />
        )}
        {isDiretorFinanceiro && (
          <button 
            className="header-finance-dashboard"
            onClick={handleFinanceDashboardClick}
            aria-label="Dashboard Financeiro"
            title="Dashboard Financeiro"
          >
            <ChartSpline size={20} />
          </button>
        )}
        
        {canAccessOperationalAssistant && (
          <button
            className={`header-assistant header-operational-assistant ${operationalAssistantOpen ? 'header-assistant-active' : ''}`}
            onClick={handleOperationalAssistantClick}
            aria-label="Assistente operacional"
            title="Assistente operacional"
          >
            <Map size={20} />
          </button>
        )}

        {canAccessSellerAssistant && (
          <button
            className={`header-assistant ${assistantOpen ? 'header-assistant-active' : ''}`}
            onClick={handleAssistantClick}
            aria-label="Assistente de vendas"
            title="Assistente de vendas"
          >
            <BrainCircuit size={20} />
          </button>
        )}

        {canAccessMessages && (
          <button 
            className="header-messages"
            onClick={handleMessagesClick}
            aria-label="Mensagens"
            title="Mensagens"
          >
            <MessageSquare size={20} />
            {unreadCount > 0 && (
              <span className="message-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
        )}
        
        <div className={`header-notifications-wrap${showNotifications ? ' is-open' : ''}`} ref={notificationsRef}>
          {showNotifications && (
            <button
              type="button"
              className="notifications-backdrop"
              aria-label="Fechar notificações"
              onClick={() => setShowNotifications(false)}
            />
          )}
          <button 
            className="header-notifications"
            onClick={() => setShowNotifications(!showNotifications)}
            aria-label="Notificações"
          >
            <Bell size={20} />
            {notificationCount > 0 && (
              <span className="notification-badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notifications-dropdown">
              {loadingNotifications && !profileReminder ? (
                <div className="notification-empty">
                  <p>Carregando...</p>
                </div>
              ) : (!profileReminder && (!notifications || notifications.length === 0)) ? (
                <div className="notification-empty">
                  <p>Nenhuma notificação no momento</p>
                </div>
              ) : (
                <>
                  <div className="notifications-header">
                    <h3>Notificações</h3>
                    {canAccessMessages && (
                      <button 
                        onClick={() => navigate('/erp/mensagens')}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#3b82f6', 
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          padding: '0.25rem 0.5rem'
                        }}
                      >
                        Ver todas
                      </button>
                    )}
                  </div>
                  <div className="notifications-list">
                    {profileReminder && (
                      <div
                        key="profile-professional"
                        className="notification-item"
                        onClick={() => handleNotificationClick(profileReminder)}
                      >
                        <div className="notification-icon">
                          <PenLine size={18} />
                        </div>
                        <div className="notification-content">
                          <strong>{profileReminder.title}</strong>
                          <p>{profileReminder.message}</p>
                          <span className="notification-time">Abrir perfil</span>
                        </div>
                      </div>
                    )}
                    {notifications && notifications.length > 0 ? (
                      notifications.map((notification) => {
                        // Tentar diferentes campos para a mensagem
                        const messageContent = notification.last_message_content 
                          || notification.content 
                          || notification.message 
                          || notification.initial_message
                          || 'Nova mensagem'
                        
                        return (
                          <div
                            key={notification.id}
                            className="notification-item"
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="notification-icon">
                              {notification.subject?.toLowerCase().includes('auditoria') ? (
                                <Package size={18} />
                              ) : (
                                <MessageSquare size={18} />
                              )}
                            </div>
                            <div className="notification-content">
                              <strong>{notification.subject || 'Sem assunto'}</strong>
                              <p>{messageContent}</p>
                              <span className="notification-time">
                                {formatTime(notification.last_message_at || notification.updated_at)}
                              </span>
                            </div>
                            {notification.unread_count > 0 && (
                              <span className="notification-unread-badge">
                                {notification.unread_count > 9 ? '9+' : notification.unread_count}
                              </span>
                            )}
                          </div>
                        )
                      })
                    ) : (
                      <div className="notification-empty">
                        <p>Nenhuma notificação no momento</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <HeaderThemeSwitch />

        <div className="header-user">
          <div className="user-info">
            <span className="user-name">{user?.name || 'Usuário'}</span>
            <span className="user-role">{user?.isAdmin ? 'Administrador' : 'Usuário'}</span>
          </div>
          <UserMenu />
        </div>
      </div>
    </header>
  )
}

export default Header

